import React from 'react';
import * as Headless from '@headlessui/react';
import { ArrowDownTrayIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@oysterjs/ui/table';
import { SearchFilterInput } from '@oysterjs/ui/search-filter';
import { Spinner } from '@oysterjs/ui/spinner';
import { Button } from '@oysterjs/ui/button';
import {
  CallFieldsFragment,
  CallReportFieldsFragment,
  OutboundCallReportFieldsFragment,
  OutboundCallStatus
} from '~/api/graphql';
import { getCalls, getCallReport, getOutboundCallReport } from '~/api/data/calls/queries';
import { titleCase } from '~/components/calls/calls-columns';
import { downloadCsv } from './calls';
import { useCallFilters } from '~/api/data/calls/calls-filters';
import { formatOutboundCallStatus } from '~/components/calls/outbound-call';

const dayFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric'
});

const reportDayKey = (date: Date | string): string => String(date);

const formatReportDay = (date: Date | string): string => dayFormatter.format(new Date(date));

const StatCard = (props: { title: string; value: string | number; subtitle?: string }) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-5 py-4">
    <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400 truncate">
      {props.title}
    </dt>
    <dd className="mt-1 text-2xl font-semibold text-neutral-900 dark:text-white">{props.value}</dd>
    {props.subtitle && (
      <dd className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{props.subtitle}</dd>
    )}
  </div>
);

const DAILY_PAGE_SIZE = 20;

const ReportSection = (props: React.PropsWithChildren<{ title: string }>) => (
  <section className="space-y-4">
    <h2 className="m-0 text-lg font-semibold text-neutral-900 dark:text-white">{props.title}</h2>
    {props.children}
  </section>
);

const CallReportDashboard = (props: { reportData: CallReportFieldsFragment }) => {
  const { dailyCounts, hangupCounts, serviceRequestTypeCounts } = props.reportData;
  const totalCalls = dailyCounts.reduce((sum, d) => sum + d.totalCalls, 0);
  const totalBusinessHours = dailyCounts.reduce((sum, d) => sum + d.businessHoursCalls, 0);
  const totalAfterHours = dailyCounts.reduce((sum, d) => sum + d.afterHoursCalls, 0);

  // Reverse so newest days are first
  const reversedDailyCounts = React.useMemo(() => [...dailyCounts].reverse(), [dailyCounts]);
  const [dailyVisible, setDailyVisible] = React.useState(DAILY_PAGE_SIZE);
  React.useEffect(() => setDailyVisible(DAILY_PAGE_SIZE), [dailyCounts]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Calls" value={totalCalls} />
        <StatCard
          title="Business Hours"
          value={totalBusinessHours}
          subtitle={
            totalCalls > 0
              ? `${Math.round((totalBusinessHours / totalCalls) * 100)}% of total`
              : undefined
          }
        />
        <StatCard
          title="After Hours"
          value={totalAfterHours}
          subtitle={
            totalCalls > 0
              ? `${Math.round((totalAfterHours / totalCalls) * 100)}% of total`
              : undefined
          }
        />
        <StatCard
          title="Caller Hangups"
          value={hangupCounts.total}
          subtitle={
            hangupCounts.underTwentySeconds > 0
              ? `${hangupCounts.underTwentySeconds} within 20s`
              : undefined
          }
        />
      </div>

      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-5 py-4">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3 mt-0">
          Caller Hangups
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400">Total Hangups</div>
            <div className="text-lg font-semibold text-neutral-900 dark:text-white">
              {hangupCounts.total}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400">Hangups Under 20s</div>
            <div className="text-lg font-semibold text-neutral-900 dark:text-white">
              {hangupCounts.underTwentySeconds}
            </div>
            {hangupCounts.total > 0 && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                {Math.round((hangupCounts.underTwentySeconds / hangupCounts.total) * 100)}% of
                hangups
              </div>
            )}
          </div>
        </div>
      </div>

      {serviceRequestTypeCounts.length > 0 && (
        <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-5 py-4">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3 mt-0">
            Request Types
          </h3>
          <div className="space-y-2">
            {(() => {
              const totalSR = serviceRequestTypeCounts.reduce((sum, s) => sum + s.count, 0);
              return serviceRequestTypeCounts.map((sr) => {
                const pct = totalSR > 0 ? Math.round((sr.count / totalSR) * 100) : 0;
                return (
                  <div key={sr.type} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-neutral-700 dark:text-neutral-300 truncate">
                      {titleCase(sr.type)}
                    </div>
                    <div className="flex-1 h-5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-sky-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-sm text-neutral-600 dark:text-neutral-400">
                      {sr.count} ({pct}%)
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {reversedDailyCounts.length > 0 && (
        <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-4 sm:px-6">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white pt-4 pb-2">
            Calls by Day
          </h3>
          <Table dense bleed className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]">
            <TableHead>
              <TableRow>
                <TableHeader className="py-3">Date</TableHeader>
                <TableHeader className="py-3 text-right">Total</TableHeader>
                <TableHeader className="py-3 text-right">Business Hours</TableHeader>
                <TableHeader className="py-3 text-right">After Hours</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {reversedDailyCounts.slice(0, dailyVisible).map((day) => (
                <TableRow key={reportDayKey(day.date)}>
                  <TableCell>{formatReportDay(day.date)}</TableCell>
                  <TableCell className="text-right font-medium">{day.totalCalls}</TableCell>
                  <TableCell className="text-right">{day.businessHoursCalls}</TableCell>
                  <TableCell className="text-right">{day.afterHoursCalls}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {dailyVisible < reversedDailyCounts.length && (
            <div className="px-4 py-1.5 text-center">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Showing {dailyVisible} of {reversedDailyCounts.length} days.{' '}
                <Headless.Button
                  type="button"
                  onClick={() => setDailyVisible((v) => v + DAILY_PAGE_SIZE)}
                  className="inline text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline font-medium"
                >
                  Load more
                </Headless.Button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OutboundCallReportDashboard = (props: { reportData: OutboundCallReportFieldsFragment }) => {
  const { dailyCounts, statusCounts, totalCalls, voicemailEnabledCount } = props.reportData;
  const completed = statusCounts.find((s) => s.status === OutboundCallStatus.Completed)?.count ?? 0;
  const failed =
    (statusCounts.find((s) => s.status === OutboundCallStatus.Failed)?.count ?? 0) +
    (statusCounts.find((s) => s.status === OutboundCallStatus.Cancelled)?.count ?? 0) +
    (statusCounts.find((s) => s.status === OutboundCallStatus.NoAnswer)?.count ?? 0);
  const scheduled =
    (statusCounts.find((s) => s.status === OutboundCallStatus.Scheduled)?.count ?? 0) +
    (statusCounts.find((s) => s.status === OutboundCallStatus.PendingConfirmation)?.count ?? 0) +
    (statusCounts.find((s) => s.status === OutboundCallStatus.Dialing)?.count ?? 0) +
    (statusCounts.find((s) => s.status === OutboundCallStatus.InProgress)?.count ?? 0);

  const reversedDailyCounts = React.useMemo(() => [...dailyCounts].reverse(), [dailyCounts]);
  const [dailyVisible, setDailyVisible] = React.useState(DAILY_PAGE_SIZE);
  React.useEffect(() => setDailyVisible(DAILY_PAGE_SIZE), [dailyCounts]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Calls" value={totalCalls} />
        <StatCard
          title="Completed"
          value={completed}
          subtitle={
            totalCalls > 0 ? `${Math.round((completed / totalCalls) * 100)}% of total` : undefined
          }
        />
        <StatCard
          title="Scheduled"
          value={scheduled}
          subtitle={
            totalCalls > 0 ? `${Math.round((scheduled / totalCalls) * 100)}% of total` : undefined
          }
        />
        <StatCard
          title="Failed"
          value={failed}
          subtitle={
            totalCalls > 0 ? `${Math.round((failed / totalCalls) * 100)}% of total` : undefined
          }
        />
      </div>

      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-5 py-4">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3 mt-0">
          Call Settings
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400">Voicemail Enabled</div>
            <div className="text-lg font-semibold text-neutral-900 dark:text-white">
              {voicemailEnabledCount}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400">Voicemail Disabled</div>
            <div className="text-lg font-semibold text-neutral-900 dark:text-white">
              {totalCalls - voicemailEnabledCount}
            </div>
            {totalCalls > 0 && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                {Math.round((voicemailEnabledCount / totalCalls) * 100)}% leave voicemail
              </div>
            )}
          </div>
        </div>
      </div>

      {statusCounts.some((s) => s.count > 0) && (
        <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-5 py-4">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3 mt-0">
            Call Status
          </h3>
          <div className="space-y-2">
            {(() => {
              const activeStatuses = statusCounts.filter((s) => s.count > 0);
              const totalStatus = activeStatuses.reduce((sum, s) => sum + s.count, 0);
              return activeStatuses.map((entry) => {
                const pct = totalStatus > 0 ? Math.round((entry.count / totalStatus) * 100) : 0;
                return (
                  <div key={entry.status} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-neutral-700 dark:text-neutral-300 truncate">
                      {formatOutboundCallStatus(entry.status)}
                    </div>
                    <div className="flex-1 h-5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-sky-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-sm text-neutral-600 dark:text-neutral-400">
                      {entry.count} ({pct}%)
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {reversedDailyCounts.length > 0 && (
        <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-4 sm:px-6">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white pt-4 pb-2">
            Calls by Day
          </h3>
          <Table dense bleed className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]">
            <TableHead>
              <TableRow>
                <TableHeader className="py-3">Date</TableHeader>
                <TableHeader className="py-3 text-right">Total</TableHeader>
                <TableHeader className="py-3 text-right">Completed</TableHeader>
                <TableHeader className="py-3 text-right">Scheduled</TableHeader>
                <TableHeader className="py-3 text-right">Failed</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {reversedDailyCounts.slice(0, dailyVisible).map((day) => (
                <TableRow key={reportDayKey(day.date)}>
                  <TableCell>{formatReportDay(day.date)}</TableCell>
                  <TableCell className="text-right font-medium">{day.totalCalls}</TableCell>
                  <TableCell className="text-right">{day.completed}</TableCell>
                  <TableCell className="text-right">{day.scheduled}</TableCell>
                  <TableCell className="text-right">{day.failed}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {dailyVisible < reversedDailyCounts.length && (
            <div className="px-4 py-1.5 text-center">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Showing {dailyVisible} of {reversedDailyCounts.length} days.{' '}
                <Headless.Button
                  type="button"
                  onClick={() => setDailyVisible((v) => v + DAILY_PAGE_SIZE)}
                  className="inline text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline font-medium"
                >
                  Load more
                </Headless.Button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const CallsReportPage = () => {
  const { filterDefinitions, setActiveFilters, initialFilters, currentFilter } = useCallFilters();

  const [inboundReportData, setInboundReportData] = React.useState<CallReportFieldsFragment | null>(
    null
  );
  const [outboundReportData, setOutboundReportData] =
    React.useState<OutboundCallReportFieldsFragment | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const csvCallsRef = React.useRef<CallFieldsFragment[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getCallReport(currentFilter).then((result) => {
      if (!cancelled && result.data && !result.error) {
        setInboundReportData(result.data);
      }
      if (!cancelled) setIsLoading(false);
    });
    getCalls(currentFilter).then((result) => {
      if (!cancelled && result.data && !result.error) {
        csvCallsRef.current = result.data;
      }
    });
    getOutboundCallReport().then((result) => {
      if (!cancelled && result.data && !result.error) {
        setOutboundReportData(result.data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentFilter]);

  return (
    <>
      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter inbound calls by voice agent, sentiment, transferred, phone, or date..."
          />
        </div>
        <Button
          type="button"
          color="white"
          className="h-12"
          disabled={isLoading}
          onClick={() => {
            if (csvCallsRef.current.length) downloadCsv(csvCallsRef.current);
          }}
        >
          <ArrowDownTrayIcon className="size-4" />
          Download CSV
        </Button>
      </div>
      {isLoading ? (
        <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
          <div className="text-center py-12">
            <Spinner size="size-8" color="neutral" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Loading report...
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              This might take a moment.
            </p>
          </div>
        </div>
      ) : inboundReportData || outboundReportData ? (
        <div className="space-y-10">
          {inboundReportData ? (
            <ReportSection title="Inbound calls">
              <CallReportDashboard reportData={inboundReportData} />
            </ReportSection>
          ) : (
            <ReportSection title="Inbound calls">
              <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-6 py-12 text-center">
                <ChartBarIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                  No inbound report data
                </h3>
              </div>
            </ReportSection>
          )}
          {outboundReportData ? (
            <ReportSection title="Outbound calls">
              <OutboundCallReportDashboard reportData={outboundReportData} />
            </ReportSection>
          ) : (
            <ReportSection title="Outbound calls">
              <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-6 py-12 text-center">
                <ChartBarIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                  No outbound report data
                </h3>
              </div>
            </ReportSection>
          )}
        </div>
      ) : (
        <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              No report data
            </h3>
          </div>
        </div>
      )}
    </>
  );
};
